// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.7
//   protoc               v5.29.6
// source: call.proto

import type { PhoneProvider } from './phone_number';

export const protobufPackage = 'compasspb';

/**
 * CallStatus represents the current state of a call in its lifecycle.
 * The status progresses from NOT_STARTED through various states to
 * a terminal state (COMPLETED, FAILED, or CANCELLED).
 */
export enum CallStatus {
  /** CALL_STATUS_UNSPECIFIED - Default/unknown status - should not be used */
  CALL_STATUS_UNSPECIFIED = 0,
  /** CALL_STATUS_SCHEDULED - Call has been scheduled for a future time */
  CALL_STATUS_SCHEDULED = 1,
  /** CALL_STATUS_IN_PROGRESS - Call is currently active/in progress */
  CALL_STATUS_IN_PROGRESS = 2,
  /** CALL_STATUS_COMPLETED - Call completed successfully */
  CALL_STATUS_COMPLETED = 3,
  /** CALL_STATUS_FAILED - Call failed due to technical issues or errors */
  CALL_STATUS_FAILED = 4,
  /** CALL_STATUS_CANCELLED - Call was cancelled before completion */
  CALL_STATUS_CANCELLED = 5,
  /** CALL_STATUS_NOT_STARTED - Call has been created but not yet started or scheduled */
  CALL_STATUS_NOT_STARTED = 6,
}

/**
 * CallType distinguishes the origin and initiator of a call.
 * This helps track whether calls were initiated by humans, AI agents,
 * or external systems, which can affect processing and routing logic.
 */
export enum CallType {
  /** CALL_TYPE_UNSPECIFIED - Default/unknown type - should not be used */
  CALL_TYPE_UNSPECIFIED = 0,
  /**
   * CALL_TYPE_HUMAN - Human-initiated calls (via HTTP/Dashboard)
   * These are calls manually created by users through the UI or API
   */
  CALL_TYPE_HUMAN = 1,
  /**
   * CALL_TYPE_AI - AI-initiated calls (via gRPC)
   * These are calls automatically created by AI agents
   */
  CALL_TYPE_AI = 2,
  /**
   * CALL_TYPE_EXTERNAL - External calls (from third-party systems)
   * These are calls initiated by external integrations or webhooks
   */
  CALL_TYPE_EXTERNAL = 3,
}

/**
 * Why a case id supplied to CreateCallStack could not be stacked.
 * UNSPECIFIED means the case is eligible.
 */
export enum CallStackRejectionReason {
  CALL_STACK_REJECTION_REASON_UNSPECIFIED = 0,
  /** CALL_STACK_REJECTION_REASON_NOT_FOUND - No such case, or not visible to the caller. */
  CALL_STACK_REJECTION_REASON_NOT_FOUND = 1,
  /** CALL_STACK_REJECTION_REASON_NOT_ASSIGNED - Assigned to somebody else, or unassigned. */
  CALL_STACK_REJECTION_REASON_NOT_ASSIGNED = 2,
  /** CALL_STACK_REJECTION_REASON_STATE_CHANGED - No longer waiting on a human call. */
  CALL_STACK_REJECTION_REASON_STATE_CHANGED = 3,
  /**
   * CALL_STACK_REJECTION_REASON_SAME_CASE - The case the call is being placed from. A stack records the OTHER cases
   * one call covered, so the parent cannot cover itself. Distinct from
   * NOT_FOUND on purpose: the caller already holds this id — it is in the
   * URL path they called — so naming it discloses nothing.
   */
  CALL_STACK_REJECTION_REASON_SAME_CASE = 4,
  /**
   * CALL_STACK_REJECTION_REASON_PAYER_MISMATCH - A different payer from the case the call was placed from, or no payer.
   * One call reaches one payer, so a stack covers that payer's cases only.
   */
  CALL_STACK_REJECTION_REASON_PAYER_MISMATCH = 5,
  /**
   * CALL_STACK_REJECTION_REASON_ON_ANOTHER_CALL - Already on another open call (its active_call_id is set and that call
   * is not in this stack). Someone is working the case.
   */
  CALL_STACK_REJECTION_REASON_ON_ANOTHER_CALL = 6,
  /**
   * CALL_STACK_REJECTION_REASON_CALL_ENDED - The call has ended. A stack's time marks where hermes cuts each case's
   * recording slice, so a case cannot join after hangup.
   */
  CALL_STACK_REJECTION_REASON_CALL_ENDED = 7,
  /**
   * CALL_STACK_REJECTION_REASON_CALL_NOT_CONNECTED - The call has not connected yet: no recording has started, so there is no
   * point to cut this case's slice from. Retry once the payer answers.
   */
  CALL_STACK_REJECTION_REASON_CALL_NOT_CONNECTED = 8,
  /**
   * CALL_STACK_REJECTION_REASON_WORKFLOW_MISMATCH - A different workflow type from the case the call was placed from. One
   * call works one workflow's questions, so a stack covers cases of the
   * parent's workflow type only. Programs may still differ.
   */
  CALL_STACK_REJECTION_REASON_WORKFLOW_MISMATCH = 9,
}

/**
 * ClaimStatus tells the browser whether a single claim poll assigned a call.
 * The browser drives the poll loop: it re-issues ClaimCall while the status is
 * NO_CALL and proceeds to AckCall once it is ASSIGNED.
 */
export enum ClaimStatus {
  CLAIM_STATUS_UNSPECIFIED = 0,
  /** CLAIM_STATUS_ASSIGNED - A call was assigned to this worker; call_id is set — proceed to AckCall. */
  CLAIM_STATUS_ASSIGNED = 1,
  /**
   * CLAIM_STATUS_NO_CALL - No call is ready yet (the scheduler returned no-calls); the browser should
   * wait briefly and poll again.
   */
  CLAIM_STATUS_NO_CALL = 2,
}

/**
 * CallDisposition captures the outcome and scheduling information for a call.
 * This includes the disposition type, when a follow-up was requested, and
 * any notes about the disposition decision.
 */
export interface CallDisposition {
  /** Type of disposition (e.g., "completed", "no_answer", "callback_requested") */
  type?: string | undefined;
  /** Timestamp when a callback or follow-up was requested */
  requested_time?: string | undefined;
  /** Additional notes about the disposition */
  notes?: string | undefined;
  /** Output data keys required for a complete disposition (e.g., ["plan_name", "member_id", "group_number"]) */
  required_fields?: string[] | undefined;
  /** Output data keys that are still missing (e.g., ["group_number"]) */
  missing_fields?: string[] | undefined;
}

/**
 * Heard markers — keys + timestamps only, NEVER field values (PHI-safe).
 * Records that a rep verbally confirmed a field's value during a live call so
 * the value can be recovered from the recording afterward.
 */
export interface CallHeardMarker {
  /** wall-clock when marked "heard" — AUDIT */
  heard_at?: string | undefined;
  /** user id (ObjectID hex) — audit, NOT PHI */
  marked_by?: string | undefined;
  /**
   * Optional skew-resilient seek anchor: ms from the client's call-connected event,
   * measured on a monotonic clock. Absent when no client anchor exists.
   */
  heard_offset_ms?: number | undefined;
}

/**
 * CallAsset contains references to assets generated from a call.
 * Assets include transcripts and audio recordings that can be
 * retrieved via the Asset service using these IDs.
 */
export interface CallAsset {
  /** Asset ID for the call transcript */
  transcript?: string | undefined;
  /** Asset ID for the call audio recording */
  recording?: string | undefined;
  /** Asset ID of the whole, unsliced call; set on a stack parent only */
  fat_recording?: string | undefined;
  /** Asset ID of the whole, unsliced call's transcript; set on a stack parent only */
  fat_transcript?: string | undefined;
}

/**
 * ChartInfo tracks call notes and the status of chart and task creation/updates.
 * This is used to manage the workflow of documenting calls in the charting system
 * and ensuring tasks are properly synchronized.
 */
export interface ChartInfo {
  /** Notes or documentation about the call for charting purposes */
  notes?: string | undefined;
  /** Indicates whether a task has been created for this call */
  task_created?: boolean | undefined;
  /** Indicates whether the associated task has been updated */
  task_updated?: boolean | undefined;
  /** Indicates whether a chart entry has been created for this call */
  chart_created?: boolean | undefined;
  /** Indicates whether the chart entry has been updated */
  chart_updated?: boolean | undefined;
}

/**
 * ExtractedDataSection represents extracted data with associated metadata.
 * Contains both the extracted data and any metadata about the extraction process.
 */
export interface ExtractedDataSection {
  /** Extracted data as a JSON structure */
  data?: { [key: string]: any } | undefined;
  /** Metadata about the extraction (e.g., extraction timestamp, source, confidence) */
  metadata?: { [key: string]: any } | undefined;
}

/**
 * ExtractedData contains structured data extracted from a call.
 * Separates input data (provided to the call) from output data (extracted from the call).
 */
export interface ExtractedData {
  /** Output data extracted from the call (e.g., from transcripts or AI analysis) */
  output?: ExtractedDataSection | undefined;
  /** Input data for the call (e.g., data provided before or during the call) */
  input?: ExtractedDataSection | undefined;
}

/**
 * CallStatusFlags provides boolean flags indicating call data extraction state.
 * Uses plain bools for HTTP responses (unlike gRPC which uses BoolValue wrappers).
 */
export interface CallStatusFlags {
  /** Whether call data extraction is currently in progress */
  call_data_extract_in_progress?: boolean | undefined;
  /** Whether call data extraction has completed */
  call_data_extract_completed?: boolean | undefined;
  /** Whether this stacked call's recording is currently being split */
  stack_split_in_progress?: boolean | undefined;
  /** Whether the split produced this call's own recording and transcript */
  stack_split_completed?: boolean | undefined;
  /** Why the split did not produce assets for this call; empty when it did */
  stack_split_failed_reason?: string | undefined;
}

/** EndReason captures why a call ended. */
export interface EndReason {
  /** Identifier of the party that terminated the call (e.g., "peer", "worker", "operator"). */
  terminated_by?: string | undefined;
}

/**
 * CallStack marks a call as part of a stacked human call: one conversation
 * covering several cases. stack_id is the id of the call that was placed; the
 * parent call points at itself. parent_case_id and parent_case_code let a
 * covered case render and link its banner with no further lookups.
 */
export interface CallStack {
  stack_id?: string | undefined;
  parent_case_id?: string | undefined;
  parent_case_code?: string | undefined;
  case_code?: string | undefined;
  /**
   * Program of THIS call's own case. A stack may span programs within one
   * organization, so a link to this case cannot assume the viewer's program.
   */
  case_program_id?: string | undefined;
  /**
   * Program of the parent case, so a covered case can link back to the case
   * the call was placed from even when it lives in another program.
   */
  parent_case_program_id?: string | undefined;
  /**
   * Who asserted this stack, and when. Both duplicate the enclosing call
   * record's user_id/created_at; that is deliberate. A stack is an assertion
   * by a person that one conversation covered these cases, and an auditor
   * reading it should not have to know a sibling field carries the same
   * meaning. Identical across every call in one stack.
   */
  created_by?: string | undefined;
  created_at?: string | undefined;
}

/**
 * CallPayerHours is the working-hours config of the payer a case's AI calls
 * dial (BV-2480), as admin Payors shows it. It is the same VendorHours policy
 * the AI call waits on before dialing: the payor whose slug equals the case's
 * payor field, and the policy for the case's workflow_type, else the default
 * one. Clients convert it to the viewer's timezone; it carries no PHI.
 */
export interface CallPayerHours {
  /**
   * IANA timezone the hours are written in, e.g. "America/New_York": the policy's
   * rule timezone, which the dialer evaluates them in, else the payor's.
   */
  timezone?: string | undefined;
  /**
   * Working hours, "Mon=08:00-17:00;Tue=08:00-17:00;...". A day that is absent
   * is closed. These gate the AI dial; sla_hours are not sent.
   */
  working_hours?: string | undefined;
  /**
   * Holidays, "MM-DD-YYYY, MM-DD-YYYY". They recur every year: only the month
   * and day are matched, the year is the current one.
   */
  holidays_list?: string | undefined;
}

/**
 * The outcome for one supplied case id. Returned for EVERY id, eligible or not,
 * so the dialog can render a resolved row (code + state) or a reason in place.
 */
export interface CallStackCandidate {
  case_record_id?: string | undefined;
  /** Set when the case resolved, so the dialog can show what the agent picked. */
  case_code?: string | undefined;
  state?: string | undefined;
  /** UNSPECIFIED means eligible. */
  reason?: CallStackRejectionReason | undefined;
}

export interface CreateCallStackRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  /** The case the call was placed from. */
  case_record_id?: string | undefined;
  /**
   * The call that was placed. This is the stack id. Required unless
   * validate_only is set — when the dialog is open no call exists yet.
   */
  call_id?: string | undefined;
  child_case_record_ids?: string[] | undefined;
  /**
   * Dry run: resolve and check every id, write nothing. This is how the dialog
   * reports a bad case id before the agent dials, rather than as a toast
   * mid-call. A client-side per-id GetCaseRecord cannot serve this — that route
   * is org/program-scoped and a typed id may belong to another program.
   */
  validate_only?: boolean | undefined;
}

export interface CreateCallStackResponse {
  /** Written cases. Always empty when validate_only is set. */
  stacked_case_record_ids?: string[] | undefined;
  /** Every supplied id with its outcome, in the order supplied. */
  candidates?: CallStackCandidate[] | undefined;
}

/**
 * One call in a stack, reduced to what a link needs. Deliberately NOT a
 * CallInfo: everything a stack member's own call holds — phone numbers,
 * input/output data, disposition and chart notes, recording and transcript
 * asset ids — is another case's data that a link to it does not need.
 */
export interface CallStackMember {
  call_id?: string | undefined;
  case_record_id?: string | undefined;
  case_code?: string | undefined;
  case_program_id?: string | undefined;
}

export interface GetCallStackRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  call_id?: string | undefined;
}

export interface GetCallStackResponse {
  /**
   * The parent call's id. A member whose call_id equals this is the call that
   * was dialled from.
   */
  stack_id?: string | undefined;
  /** Every call in the stack, including the parent. Callers filter themselves out. */
  members?: CallStackMember[] | undefined;
}

/**
 * A case the caller could stack onto a call, carrying only what the dialpad
 * card shows.
 */
export interface StackableCase {
  case_record_id?: string | undefined;
  case_code?: string | undefined;
  /** The case's own program; a stack may span programs of one organization. */
  program_id?: string | undefined;
  program_name?: string | undefined;
  payer_slug?: string | undefined;
  payer_name?: string | undefined;
  state?: string | undefined;
  /**
   * Seconds of SLA left at response time; negative once breached.
   * Meaningless when has_sla is false.
   */
  sla_remaining_seconds?: number | undefined;
  sla_breached?: boolean | undefined;
  /** False when the case's workflow has no SLA budget. */
  has_sla?: boolean | undefined;
  /** Already stacked on this call. Listed so the card stays in place, locked. */
  in_this_stack?: boolean | undefined;
}

export interface ListStackableCasesRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  /** The case the call was placed from. */
  case_record_id?: string | undefined;
  /** The call. This is the stack id. */
  call_id?: string | undefined;
}

export interface ListStackableCasesResponse {
  /**
   * Sorted by SLA: breached first (most overdue first), then by time left;
   * cases without an SLA last.
   */
  cases?: StackableCase[] | undefined;
  /** Cases not yet in this stack. */
  stackable_count?: number | undefined;
  /** The call's own case has no payer slug, so nothing can match it. */
  parent_payer_missing?: boolean | undefined;
  /** The call has ended; nothing can be stacked onto it. */
  call_ended?: boolean | undefined;
}

/**
 * CallInfo represents the public view of a call record.
 * This message excludes internal fields like org_id, callflow_id, engagement_id,
 * host, and worker_addr which are only used for internal system operations.
 */
export interface CallInfo {
  /** Unique identifier for the call */
  id?: string | undefined;
  /** ID of the case record this call is associated with */
  caserecord_id?: string | undefined;
  /**
   * Provider-side call identifier used to track the call with the carrier
   * (a Twilio Call SID, or a Telnyx call control id)
   */
  sid?: string | undefined;
  /** Current status of the call */
  status?: CallStatus | undefined;
  /** Phone number being called (customer/patient phone) */
  phone?: string | undefined;
  /** Outbound caller ID phone number shown to the recipient */
  out_phone?: string | undefined;
  /** Input data provided to the call (e.g., variables for call script) */
  input_data?: { [key: string]: any } | undefined;
  /** Disposition information capturing call outcome */
  disposition?: CallDisposition | undefined;
  /** Numeric disposition type code for categorization */
  disposition_type?: number | undefined;
  /** Output data extracted from the call (e.g., collected information) */
  output_data?: { [key: string]: any } | undefined;
  /**
   * Background-model (voice agent) raw extraction, recorded for reference only.
   * Nothing reads this for logic. For pbm calls the authoritative extraction
   * lives in output_data (written by hermes PCE); the agent's own extraction
   * is parked here.
   */
  bg_model_output_data?: { [key: string]: any } | undefined;
  /** Timestamp when the call is scheduled to occur */
  scheduled_at?: string | undefined;
  /** Timestamp when the call actually started */
  started_at?: string | undefined;
  /** Timestamp when the call ended */
  ended_at?: string | undefined;
  /** Custom identifier field 0 for flexible identification */
  identifier_0?: string | undefined;
  /** Custom identifier field 1 for flexible identification */
  identifier_1?: string | undefined;
  /** Custom identifier field 2 for flexible identification */
  identifier_2?: string | undefined;
  /** Timestamp when the call record was created */
  created_at?: string | undefined;
  /** Timestamp when the call record was last updated */
  updated_at?: string | undefined;
  /** List of assets generated from the call (transcripts and recordings) */
  assets?: CallAsset[] | undefined;
  /** Type of call indicating its origin (human, AI, or external) */
  type?: CallType | undefined;
  /**
   * Case state at call creation time (e.g., "primary_human_audit", "human_audit")
   * This field is immutable after creation
   */
  case_state?: string | undefined;
  /** Call charting information including notes and task tracking */
  chart_info?: ChartInfo | undefined;
  /** Extracted data from the call with separate input and output sections */
  extracted_data?: ExtractedData | undefined;
  /** Status flags for call data extraction state */
  status_flags?: CallStatusFlags | undefined;
  /**
   * ID of the user who made/handled the call. For human calls this is stamped
   * server-side from the authenticated creator and is immutable afterwards.
   */
  user_id?: string | undefined;
  /** ID of the agent that handled the call */
  agent_id?: string | undefined;
  /** Reason the call ended */
  end_reason?: EndReason | undefined;
  /**
   * Heard markers: map from output field path (e.g. "member_id") to the
   * timestamp+user when a rep verbally confirmed the field during the call.
   * Keys and timestamps only — no field values are stored here (PHI-safe).
   */
  heard_markers?: { [key: string]: CallHeardMarker } | undefined;
  /**
   * Stacked-call membership: set when this call is part of a stacked human
   * call (one conversation covering several cases).
   */
  call_stack?: CallStack | undefined;
  /**
   * Telephony provider that placed this call.
   *
   * Denormalised onto the call rather than derived by joining out_phone back
   * to the phone number registry: out_phone can be absent when a status
   * webhook arrives, and the provider decides how a token is minted, how a
   * webhook is verified and how a recording is authenticated.
   */
  provider?: PhoneProvider | undefined;
  /**
   * The case's CURRENT workflow_type, resolved at read time via a join —
   * unlike case_state (field 21), which is a frozen snapshot from call
   * creation. Populated only by list endpoints that opt into the join (e.g.
   * GetMyUserCalls); empty when not resolved. Generic: consumers interpret it
   * per domain (e.g. the AE/PQC UI treats a value other than
   * "aepqc_preprocessing" as "the case has already been created").
   */
  case_workflow_type?: string | undefined;
  schema_version?: string | undefined;
  /**
   * The working hours of the payer this case's calls dial (BV-2480). Set only
   * by GetCaseRecordCalls; unset when the case names no payor, no payor has
   * that slug, or the payor has no hours config for the case's workflow type
   * and no default one.
   */
  payer_hours?: CallPayerHours | undefined;
  /**
   * Typed call-scoped data written by system callers over the core API
   * (AA-1666). Read-only here: no public request accepts it. The dashboard
   * passes metadata.program_config.flags to the disposition engine.
   */
  metadata?: CallMetadata | undefined;
}

export interface CallInfo_HeardMarkersEntry {
  key: string;
  value?: CallHeardMarker | undefined;
}

/**
 * CallMetadata is the read-only public view of a call's typed metadata. Each
 * top-level key is a named field.
 */
export interface CallMetadata {
  /** Program flags bvagent resolved for an AI call; unset on human calls. */
  program_config?: ProgramConfig | undefined;
}

/**
 * ProgramConfig is the program flag set an AI call was made with, saved on the
 * call so the disposition engine grades it with the same flags. Program
 * configuration only, never PHI.
 */
export interface ProgramConfig {
  /**
   * Resolved flags, flag name -> value. The shape is owned by the flag
   * registry in bvagent and the disposition engine.
   */
  flags?: { [key: string]: any } | undefined;
  resolved_at?: string | undefined;
  /** dynconfig Entry.version of the program's program_config key. */
  config_version?: number | undefined;
}

/**
 * CreateCallRequest is used to create a new call record associated with a case record.
 * The call can be scheduled for later or initiated immediately based on the case flow.
 */
export interface CreateCallRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  phone?: string | undefined;
  out_phone?: string | undefined;
  input_data?: { [key: string]: any } | undefined;
  /**
   * Deprecated: ignored — the server stamps user_id from the authenticated caller.
   *
   * @deprecated
   */
  user_id?: string | undefined;
  /**
   * When true, the call is a proxied (WebSocket) call: after the call record
   * is created, Compass submits it to the call-scheduler queue (under the
   * call_id as the agent id) so the browser worker can claim it. The normal
   * call response is still returned.
   */
  proxied?: boolean | undefined;
  /**
   * For proxied calls: the scheduler lane the browser generated for this call.
   * The same value is echoed back on ClaimCall/AckCall so the worker poll/ack
   * bind to the same lane and the call routes back to this browser. Empty for
   * non-proxied calls.
   */
  lane?: string | undefined;
  /**
   * Telephony provider to place this call on. Must match the provider of the
   * out_phone registry entry, and the server must hold credentials for it.
   */
  provider?: PhoneProvider | undefined;
}

/** CreateCallResponse returns the newly created call information. */
export interface CreateCallResponse {
  /** The created call record with all fields populated */
  call?: CallInfo | undefined;
}

/**
 * ClaimCallRequest long-polls the call-scheduler for a call assigned to an agent.
 * The agent id is derived server-side from the call's workflow type and is never
 * supplied by the client.
 */
export interface ClaimCallRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
  /**
   * The client-generated scheduler lane (the same value sent on CreateCall).
   * Bound to the worker poll as the x-canary-lane header so this claim only
   * sees the call submitted on this lane.
   */
  lane?: string | undefined;
}

/**
 * ClaimCallResponse reports the result of a single (non-blocking) claim poll.
 * The browser polls repeatedly: on CLAIM_STATUS_NO_CALL it waits and re-claims;
 * on CLAIM_STATUS_ASSIGNED it sends AckCall to receive the pod-routed WebSocket
 * attach URL (AckCallResponse.attach_url) — that URL is only known once the
 * worker acks.
 */
export interface ClaimCallResponse {
  call_id?: string | undefined;
  /**
   * Whether this poll assigned a call. The browser re-claims while NO_CALL and
   * proceeds to AckCall on ASSIGNED.
   */
  status?: ClaimStatus | undefined;
}

/**
 * AckCallRequest confirms the worker is ready for the claimed call. The agent id
 * is derived server-side from the call's workflow type, never supplied by the client.
 */
export interface AckCallRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
  /**
   * The client-generated scheduler lane (the same value sent on CreateCall).
   * Bound to the worker ack as the x-canary-lane header so the ack lands on the
   * same lane/pod as the claim.
   */
  lane?: string | undefined;
}

/**
 * AckCallResponse reports whether the acknowledgement succeeded and carries the
 * pod-routed WebSocket attach URL the browser connects to for media.
 */
export interface AckCallResponse {
  ok?: boolean | undefined;
  message?: string | undefined;
  attach_url?: string | undefined;
}

/**
 * CancelCallRequest cancels a proxied call abandoned before it connected. The
 * agent id is derived server-side from the call's workflow type; the scheduler
 * cancel is keyed by call_id (no lane needed).
 */
export interface CancelCallRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
}

/**
 * CancelCallResponse reports whether the cancel was accepted. The operation is
 * idempotent — cancelling an already-terminal call still reports ok.
 */
export interface CancelCallResponse {
  ok?: boolean | undefined;
}

/** GetCallRequest is used to retrieve a specific call by its ID. */
export interface GetCallRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
}

/** GetCallResponse returns the requested call information excluding internal fields. */
export interface GetCallResponse {
  /** The requested call record */
  call?: CallInfo | undefined;
}

/**
 * UpdateCallRequest is used to update a call's status, disposition, input/output data,
 * and charting information. All fields are optional - only provided fields will be updated.
 */
export interface UpdateCallRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
  input_data?: { [key: string]: any } | undefined;
  output_data?: { [key: string]: any } | undefined;
  bg_model_output_data?: { [key: string]: any } | undefined;
  disposition?: CallDisposition | undefined;
  disposition_type?: number | undefined;
  chart_info?: ChartInfo | undefined;
  /**
   * Deprecated: rejected with INVALID_ARGUMENT — user_id is stamped from the
   * authenticated caller at creation and is immutable afterwards.
   *
   * @deprecated
   */
  user_id?: string | undefined;
  agent_id?: string | undefined;
  end_reason?: EndReason | undefined;
  /**
   * Heard markers: map from output field path to heard timestamp+user.
   * PHI-safe — stores only keys and audit metadata, never field values.
   */
  heard_markers?: { [key: string]: CallHeardMarker } | undefined;
}

export interface UpdateCallRequest_HeardMarkersEntry {
  key: string;
  value?: CallHeardMarker | undefined;
}

/** UpdateCallResponse returns the updated call information. */
export interface UpdateCallResponse {
  /** The updated call record with all current values */
  call?: CallInfo | undefined;
}

/**
 * GetCaseRecordCallsRequest is used to retrieve all calls associated with a case record.
 * Supports pagination via limit and offset parameters.
 */
export interface GetCaseRecordCallsRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  limit?: number | undefined;
  start_after_id?: string | undefined;
}

/** GetPayerHoursRequest names the payer and workflow type to read hours for. */
export interface GetPayerHoursRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  /** The payor slug a case names in its payor field (admin Payors' slug). */
  payer_slug?: string | undefined;
  /**
   * The case's workflow type. Picks the payor's hours config for that type,
   * else its default one.
   */
  workflow_type?: string | undefined;
}

/** GetPayerHoursResponse carries the payer's hours. */
export interface GetPayerHoursResponse {
  /**
   * Unset when no payor has that slug, or the payor has no hours config for
   * the workflow type and no default one.
   */
  payer_hours?: CallPayerHours | undefined;
}

/** GetCaseRecordCallsResponse returns a paginated list of calls for a case record. */
export interface GetCaseRecordCallsResponse {
  /** List of calls associated with the case record */
  calls?: CallInfo[] | undefined;
  /** Total number of calls available (across all pages) */
  total?: number | undefined;
}

/**
 * GenerateAccessTokenRequest is used to generate an access token for
 * browser-based voice calls. Which provider issues the token, and therefore
 * which browser SDK can use it, follows the provider stored on the call.
 */
export interface GenerateAccessTokenRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
  /**
   * Optional outgoing phone number this token will be used with. When set, it is
   * validated against the organization's phone number registry and its provider
   * is checked against the provider stored on the call.
   */
  phone?: string | undefined;
}

/**
 * GenerateAccessTokenResponse returns a voice SDK access token, its expiration
 * time, and the provider that issued it.
 */
export interface GenerateAccessTokenResponse {
  /** Access token for authenticating with the provider's browser voice SDK */
  token?: string | undefined;
  /** Unix timestamp (seconds since epoch) when the token expires */
  expires_at?: number | undefined;
  /**
   * Provider that issued the token. The browser must load this provider's SDK:
   * a token is only usable against the carrier that issued it.
   */
  provider?: PhoneProvider | undefined;
}

/**
 * ExtractOutputRequest is used to extract structured output data from a call's
 * transcript using AI-powered field extraction. This analyzes the conversation
 * and extracts relevant data points based on configured extraction rules.
 */
export interface ExtractOutputRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  call_id?: string | undefined;
}

/**
 * ExtractOutputResponse returns the structured data extracted from the call transcript.
 * The output_data contains field values extracted using AI analysis of the conversation.
 */
export interface ExtractOutputResponse {
  /** Structured data extracted from the call transcript */
  output_data?: { [key: string]: any } | undefined;
  /** ID of the call the output was extracted from */
  call_id?: string | undefined;
}

/**
 * GetMyUserCallsRequest lists calls assigned to the authenticated user.
 * User ID is determined from the authentication context.
 */
export interface GetMyUserCallsRequest {
  /** Optional: filter by call status string ("in_progress", "completed", etc.) */
  status?: string | undefined;
  /** Optional: filter by call type ("human_call", "ai_call", "external_call") */
  type?: string | undefined;
  /** Optional: filter by case record ID */
  caserecord_id?: string | undefined;
  /** Pagination limit (default 50, max 100) */
  limit?: number | undefined;
  /** Cursor-based pagination: ID of the last call from the previous page */
  start_after_id?: string | undefined;
}

/** GetMyUserCallsResponse returns a paginated list of calls for the authenticated user. */
export interface GetMyUserCallsResponse {
  /** List of calls assigned to the user */
  calls?: CallInfo[] | undefined;
  /** Cursor for fetching the next page (empty if no more results) */
  next_cursor?: string | undefined;
}

/**
 * ListMyOrganizationCallsRequest filters calls assigned to the authenticated user
 * across all programs in an organization.
 */
export interface ListMyOrganizationCallsRequest {
  /** Organization ID (path parameter) */
  organization_id?: string | undefined;
  /** Optional program IDs filter; if empty, includes all programs in the org. */
  program_ids?: string[] | undefined;
  /** Optional case record IDs filter; if empty, no case-record ID filter is applied. */
  case_record_ids?: string[] | undefined;
  /** Filter calls created after this timestamp (exclusive). */
  created_after?: string | undefined;
  /** Filter calls created before this timestamp (exclusive). */
  created_before?: string | undefined;
  /** Pagination limit (default 50, max 100). */
  limit?: number | undefined;
  /** Cursor-based pagination - call ID to start after. */
  start_after_id?: string | undefined;
}

/** ListMyOrganizationCallsResponse returns a paginated list of calls for the authenticated user. */
export interface ListMyOrganizationCallsResponse {
  calls?: CallInfo[] | undefined;
  next_cursor?: string | undefined;
}

/** GetCallsRequest lists calls for an organization/program with optional filtering. */
export interface GetCallsRequest {
  /** Organization ID (path parameter) */
  organization_id?: string | undefined;
  /** Program ID (path parameter) */
  program_id?: string | undefined;
  /** Optional: filter calls created at or after this RFC3339 timestamp */
  from_time?: string | undefined;
  /** Optional: filter calls created at or before this RFC3339 timestamp */
  to_time?: string | undefined;
  /** Optional: filter by call status string ("in_progress", "completed", etc.) */
  status?: string | undefined;
  /** Optional: filter by call type ("human_call", "ai_call", "external_call") */
  type?: string | undefined;
  /** Pagination limit (default 50, max 100) */
  limit?: number | undefined;
  /** Cursor-based pagination: ID of the last call from the previous page */
  start_after_id?: string | undefined;
  /** Optional: filter by specific case record IDs (max 100) */
  case_record_ids?: string[] | undefined;
  /** Optional: filter by exact call ID */
  call_id?: string | undefined;
  /** Optional: filter by exact case record ID (single) */
  case_record_id?: string | undefined;
}

/** GetCallsResponse returns a paginated list of calls for the program. */
export interface GetCallsResponse {
  /** List of calls */
  calls?: CallInfo[] | undefined;
  /** Cursor for fetching the next page (empty if no more results) */
  next_cursor?: string | undefined;
}

/**
 * AdminSearchCallsRequest is the request for cross-organization call search.
 * All filter fields are optional and AND-combined.
 */
export interface AdminSearchCallsRequest {
  /**
   * When non-empty, performs an ID lookup. Other filters still apply to
   * the matched calls (so the caller can, e.g., narrow by status).
   */
  call_ids?: string[] | undefined;
  /** Exact match against either `phone` or `out_phone`. */
  phone?: string | undefined;
  /** Exact Twilio SID. */
  sid?: string | undefined;
  /** Filter to calls associated with a specific case record. */
  caserecord_id?: string | undefined;
  /** IN filter — one of the listed CallStatus values must match. */
  statuses?: CallStatus[] | undefined;
  /** IN filter — one of the listed CallType values must match. */
  types?: CallType[] | undefined;
  /** Filter calls created after this timestamp. */
  created_at_after?: string | undefined;
  /** Filter calls created before this timestamp. */
  created_at_before?: string | undefined;
  /**
   * Cursor for pagination (matches existing call.Find semantics — exclusive
   * start-after by _id).
   */
  start?: string | undefined;
  /** Maximum results to return (default: 20, max: 100). */
  limit?: number | undefined;
  /**
   * IN filter — the call's attributed user (`user_id`) is one of the listed
   * user IDs. Used, e.g., to list all calls attributed to a user account.
   */
  user_ids?: string[] | undefined;
}

/** AdminSearchCallsResponse returns the matching calls plus a pagination cursor. */
export interface AdminSearchCallsResponse {
  calls?: CallInfo[] | undefined;
  /** Cursor for the next page (empty if no more results). */
  next_cursor?: string | undefined;
}

/**
 * AdminUpdateCallRequest updates a single call by ID across any organization.
 * Mirrors the internal core UpdateCall field set. All fields except call_id are
 * optional. When update_mask is set, only the listed paths are applied;
 * otherwise every provided non-zero field is applied.
 */
export interface AdminUpdateCallRequest {
  /** REQUIRED: The call to update (path parameter). */
  call_id?: string | undefined;
  /** Fields to update. When empty, all provided non-zero fields are applied. */
  update_mask?: string[] | undefined;
  status?: CallStatus | undefined;
  phone?: string | undefined;
  out_phone?: string | undefined;
  input_data?: { [key: string]: any } | undefined;
  disposition?: CallDisposition | undefined;
  disposition_type?: number | undefined;
  output_data?: { [key: string]: any } | undefined;
  scheduled_at?: string | undefined;
  started_at?: string | undefined;
  ended_at?: string | undefined;
  host?: string | undefined;
  worker_addr?: string | undefined;
  sid?: string | undefined;
  org_id?: string | undefined;
  task_type?: string | undefined;
  callflow_id?: string | undefined;
  engagement_id?: string | undefined;
  identifier_0?: string | undefined;
  identifier_1?: string | undefined;
  identifier_2?: string | undefined;
  assets?: CallAsset[] | undefined;
  chart_info?: ChartInfo | undefined;
  agent_id?: string | undefined;
  end_reason?: EndReason | undefined;
  user_id?: string | undefined;
  /** Background-model raw extraction (record-only; nothing reads it for logic). */
  bg_model_output_data?: { [key: string]: any } | undefined;
}

/** AdminUpdateCallResponse returns the updated call. */
export interface AdminUpdateCallResponse {
  call?: CallInfo | undefined;
}

/**
 * CallService provides operations for managing calls and voice operations.
 * Calls are outbound voice interactions associated with case records.
 */
export interface CallService {
  /** CreateCall creates a new call record for a case record. */
  createCall(request: CreateCallRequest): Promise<CreateCallResponse>;
  /**
   * CreateCallStack records which other cases one human call covered.
   *
   * The literal "call-stack" segment must stay ahead of the sibling
   * /calls/{call_id} routes in both the mux registration order and the authz
   * route table; the authz table sorts by literal-segment count, so this
   * binding wins there automatically.
   *
   * Permission note: the service layer deliberately does not require the
   * parent case to be assigned to the caller (see service/call_stack.go) —
   * parent-level authorization is this annotation's job. case_record:update
   * at scope "assigned" means a program-scoped grant passes unconditionally,
   * while a caller holding only assigned-scope access must own the parent
   * case. Children are additionally gated on assignment by the service.
   */
  createCallStack(request: CreateCallStackRequest): Promise<CreateCallStackResponse>;
  /**
   * GetCallStack returns the members of the stack the given call belongs to,
   * carrying only the four fields a banner or popover renders: the call id,
   * its case record, that case's code, and that case's program. It exists so
   * that reading a stack no longer means fetching whole CallInfo records —
   * phone numbers, input/output data, disposition and chart notes, recording
   * and transcript asset ids — for every case one conversation covered.
   *
   * The path program is used ONLY to IDOR-check {call_id}. The membership
   * lookup is scoped to the ORGANIZATION and deliberately not to the program:
   * a stack may span programs of one organization (see service/call_stack.go),
   * and filtering those members out would understate the parent's count. A
   * stack can never span organizations — the write gate rejects an out-of-org
   * child — so an org-scoped lookup cannot cross tenants even in principle.
   *
   * What that org scoping discloses, stated plainly: a caller holding
   * program-read on the entry call learns the case codes and programs of
   * stacked cases sitting in programs they may hold no grant on. That is the
   * deliberate trade. Filtering those members out would make the parent's
   * count understate what the call actually covered, which reads to an auditor
   * as a false claim about the record. The exposure is bounded — it reaches
   * only cases a colleague deliberately stacked onto a call the caller can
   * already read — and the write side carries the identical property, already
   * documented in service/call_stack.go.
   *
   * Route ordering: /calls/{call_id}/stack is registered ahead of the sibling
   * /calls routes in server/http/api/routes.go, and the authz table sorts by
   * literal-segment count, so this binding wins there automatically.
   */
  getCallStack(request: GetCallStackRequest): Promise<GetCallStackResponse>;
  /**
   * ListStackableCases returns the cases the caller could stack onto this call:
   * the same organization as the call's case (any program), assigned to the
   * caller, waiting on a human call, for the same payer, and not on another
   * open call. Cases already in this stack are included and flagged. Sorted by
   * SLA, most urgent first. The same rule gates CreateCallStack, so a listed
   * case is one the save accepts. Authorized exactly like CreateCallStack.
   */
  listStackableCases(request: ListStackableCasesRequest): Promise<ListStackableCasesResponse>;
  /** GetCall retrieves call information by call ID. */
  getCall(request: GetCallRequest): Promise<GetCallResponse>;
  /**
   * GetPayerHours returns a payer's working hours for a workflow type (BV-2525),
   * the same hours GetCaseRecordCalls puts on each call (BV-2480). The
   * Workforce Console fetches it when its clock popover opens, keyed by
   * payer and workflow type so cases with the same payer share one request.
   */
  getPayerHours(request: GetPayerHoursRequest): Promise<GetPayerHoursResponse>;
  /** UpdateCall updates call information including status, SID, and disposition. */
  updateCall(request: UpdateCallRequest): Promise<UpdateCallResponse>;
  /** GetCaseRecordCalls retrieves all calls associated with a case record. */
  getCaseRecordCalls(request: GetCaseRecordCallsRequest): Promise<GetCaseRecordCallsResponse>;
  /**
   * GenerateAccessToken generates a browser voice SDK access token for a call,
   * issued by the telephony provider that call belongs to.
   */
  generateAccessToken(request: GenerateAccessTokenRequest): Promise<GenerateAccessTokenResponse>;
  /** GetMyUserCalls lists calls assigned to the authenticated user with optional filtering. */
  getMyUserCalls(request: GetMyUserCallsRequest): Promise<GetMyUserCallsResponse>;
  /**
   * ListMyOrganizationCalls lists calls assigned to the authenticated user
   * across all programs in an organization. Supports filtering by program_ids,
   * case_record_ids, and creation date range with cursor-based pagination. [AD-2203]
   */
  listMyOrganizationCalls(
    request: ListMyOrganizationCallsRequest,
  ): Promise<ListMyOrganizationCallsResponse>;
  /** GetCalls lists all calls for an organization/program with optional date range and filters. */
  getCalls(request: GetCallsRequest): Promise<GetCallsResponse>;
  /** ExtractOutput extracts structured output data from a call's transcript using AI. */
  extractOutput(request: ExtractOutputRequest): Promise<ExtractOutputResponse>;
  /**
   * ClaimCall brokers a PollCall to the call-scheduler WorkerService: it
   * long-polls for a call assigned to agent_id and returns the scheduler
   * call_id plus the WebSocket attach URL the browser connects to. Compass
   * cannot expose gRPC to the browser, so it fronts the scheduler here.
   */
  claimCall(request: ClaimCallRequest): Promise<ClaimCallResponse>;
  /**
   * AckCall brokers an AckCall to the call-scheduler WorkerService: it
   * confirms the worker is ready for the claimed call, after which the
   * scheduler dials the customer.
   */
  ackCall(request: AckCallRequest): Promise<AckCallResponse>;
  cancelCall(request: CancelCallRequest): Promise<CancelCallResponse>;
}

/**
 * AdminCallService provides admin-level cross-organization call search.
 * Restricted to system-admin users with global scope.
 */
export interface AdminCallService {
  /**
   * AdminSearchCalls searches calls globally (across all organizations and
   * programs) on behalf of system administrators.
   *
   * Two modes:
   *   - ID lookup: provide call_ids to fetch specific calls by ID.
   *   - Filter search: combine any of phone, sid, caserecord_id, user_ids,
   *     statuses, types, and created_at range. Cursor pagination via
   *     start + limit.
   */
  adminSearchCalls(request: AdminSearchCallsRequest): Promise<AdminSearchCallsResponse>;
  /**
   * AdminUpdateCall updates any call across all organizations on behalf of
   * system administrators. This is an operational override for correcting
   * stuck or mislabeled calls; it mirrors the internal core UpdateCall field
   * set and is update_mask driven. Restricted to system-admin users with
   * global scope. WARNING: this can mutate PHI — do NOT call from
   * application code or user-facing flows.
   */
  adminUpdateCall(request: AdminUpdateCallRequest): Promise<AdminUpdateCallResponse>;
}
